import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { registerDataValidationErrorMatcher } from 'errors/dataValidationError';
import useKeyDownListener from 'hooks/useKeyDownListener';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import FormField from 'layout/molecules/FormField/FormField';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import Grid from 'layout/molecules/Grid/Grid';
import InvitationSender from 'layout/molecules/InvitationSender';
import { addChannel, selectInboxStatus } from 'store/inboxSlice';
import { closeModal, selectModalByName } from 'store/layoutSlice';
import useChannelValidation from '../useChannelValidation';
function ChannelCreator({ edit, onClose }) {
    var _a;
    const { t } = useTranslation(['chat', 'common', 'specification']);
    const dispatch = useDispatch();
    const channelData = (_a = useSelector(selectModalByName(edit ? 'channelEditor' : 'channelCreator'))) === null || _a === void 0 ? void 0 : _a.data;
    const [channelForm, setChannelForm] = useState({
        name: '',
        description: '',
    });
    const [selectedUsers, setSelectedUsers] = useState([]);
    const status = useSelector(selectInboxStatus);
    const currentUserId = useSelector((state) => { var _a; return (_a = state.user.data) === null || _a === void 0 ? void 0 : _a.id; });
    const statusAdd = status.addChannel;
    const statusUpdate = status.updateChannel;
    const { errors, isValid, pendingFields, markTouched, validateAll } = useChannelValidation(channelForm, selectedUsers, t);
    useEffect(() => {
        if (edit) {
            setChannelForm({
                name: channelData.name,
                description: channelData.description,
            });
        }
    }, [edit, channelData === null || channelData === void 0 ? void 0 : channelData.name, channelData === null || channelData === void 0 ? void 0 : channelData.description]);
    const handleFormChange = ({ target: { name, value }, }) => {
        setChannelForm((channel) => (Object.assign(Object.assign({}, channel), { [name]: value })));
    };
    const handleChannelAdd = () => {
        if (!validateAll())
            return;
        dispatch(addChannel(Object.assign(Object.assign({}, channelForm), { userIds: selectedUsers }))).then(() => {
            dispatch(closeModal({ name: 'channelCreator' }));
        });
    };
    const handleChannelEdit = () => {
        // TODO: Editing channel
    };
    const handleSave = () => {
        if (edit) {
            handleChannelEdit();
        }
        else {
            handleChannelAdd();
        }
    };
    registerDataValidationErrorMatcher();
    useKeyDownListener([
        { code: 'Enter', callback: handleSave },
        { code: 'Escape', callback: onClose },
    ]);
    return (React.createElement(Modal, { name: edit ? 'channelEditor' : 'channelCreator', size: "s", header: React.createElement(Label, { weight: "strong" }, edit ? t('EDITING_CHANNEL') : t('ADDING_NEW_CHANNEL')), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: statusAdd === 'loading' || statusUpdate === 'loading', pendingFields: pendingFields, onSave: handleSave, onCancel: () => dispatch(closeModal({ name: edit ? 'channelEditor' : 'channelCreator' })), validateAll: validateAll }) },
        React.createElement(Grid, null,
            React.createElement(FormField, { label: t('CHANNEL_NAME'), error: errors.name },
                React.createElement(Input, { value: channelForm === null || channelForm === void 0 ? void 0 : channelForm.name, onChange: handleFormChange, onBlur: () => markTouched('name'), name: "name", error: Boolean(errors.name), autoFocus: true })),
            React.createElement(FormField, { label: t('CHANNEL_DESCRIPTION') },
                React.createElement(Input, { value: channelForm === null || channelForm === void 0 ? void 0 : channelForm.description, onChange: handleFormChange, name: "description" })),
            React.createElement(FormField, { label: t('SELECT_MEMBERS'), error: errors.members },
                selectedUsers.length > 0 && (React.createElement("div", { style: {
                        marginBottom: '8px',
                        fontSize: '14px',
                        color: '#666',
                        fontWeight: 500,
                        padding: '4px 8px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        border: '1px solid #e0e0e0',
                    } },
                    selectedUsers.length,
                    ' ',
                    selectedUsers.length === 1 ? t('MEMBER_SELECTED') : t('MEMBERS_SELECTED'))),
                React.createElement(InvitationSender, Object.assign({ onConfirm: () => { }, onSelectUser: (user, isSelected) => {
                        if (isSelected) {
                            setSelectedUsers((prev) => [...prev, user.id]);
                        }
                        else {
                            setSelectedUsers((prev) => prev.filter((id) => id !== user.id));
                        }
                        markTouched('members');
                    }, onClose: () => { } }, { currentUserId }))))));
}
export default memo(ChannelCreator);
